import { useContext } from "react";

import { IMlPart } from "../../types/generalTypes";
import clsx from "clsx";
import { UnitContext } from "../../contexts/contextExporter";
import { TPartMode } from "../../types/unitTypes";
import { partModeLabels } from "../../utils/static";
import { useAuth } from "../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { qoPartPracticesHistory, qoPartPractices } from "../../utils/queryOptions";
import { CheckCircle } from "@phosphor-icons/react";

export default function UnitNavPart({
  part,
  partOrder,
  isPartActive,
}: {
  part: IMlPart;
  partOrder: number;
  isPartActive: boolean;
}) {
  const { pin, setPinWithEmit, canNavigate, unit, currentMl } =
    useContext(UnitContext);
  const { isInstructor, isFulltimeSelfUser, user } = useAuth();

  const { data: scores } = useQuery(
    qoPartPracticesHistory({ part_id: part.id, user_id: user?.id || 0 })
  );

  const isCompleted = scores
    ? Object.values(scores).length > 0 &&
      Object.values(scores).every((s) => s !== null && s >= 0.79)
    : false;

  // Bu part'ın uygulaması var mı? (sadece aktif part için sorgula)
  // Uygulaması olmayan part'larda "Uygulama" sekmesi gösterilmemeli: tıklanınca
  // boş yükleyip otomatik sonraki bölüme atlıyordu (öğrenci "uygulama açılmıyor"
  // diyor). Burak part'a uygulama eklerse sekme otomatik geri gelir.
  const { data: partPractices } = useQuery({
    ...qoPartPractices({ part_id: part.id }),
    enabled: isPartActive && part.id > -1,
  });
  const hasPractice = (partPractices?.length ?? 0) > 0;

  const handlePartClick = (partMode: TPartMode) => {
    if (canNavigate) {
      setPinWithEmit({
        mlOrder: pin!.mlOrder,
        partMode: partMode,
        partOrder,
      });
    }
  };

  const shouldContentVisible =
    ![null, "NOT_STARTED", undefined].includes(unit?.unit_live_status) ||
    isInstructor || isFulltimeSelfUser;

  return (
    <div className="py-0.5 pl-3 relative">
      {/* Left accent */}
      <div
        className={clsx(
          "absolute h-full transition-all rounded-full left-0 top-0",
          isPartActive
            ? "bg-white/40 w-[2px]"
            : "bg-white/10 w-px"
        )}
      />
      <div
        onClick={() =>
          handlePartClick(
            part.id === -1 || currentMl?.is_zoom ? "ZOOM" : "VIDEO"
          )
        }
        className={clsx(
          "flex items-center gap-1.5 py-1",
          isPartActive ? "text-white font-medium" : "text-white/40",
          !isPartActive &&
          canNavigate &&
          "cursor-pointer hover:text-white/60 transition-colors"
        )}
      >
        {isCompleted && (
          <CheckCircle size={14} weight="fill" className="text-myGreen shrink-0" />
        )}
        <span className="text-xs">{part.part_title}</span>
      </div>
      {shouldContentVisible && isPartActive && part.id > -1 && hasPractice && (
        <div className="inline-flex mt-1 mb-1 bg-white/5 border border-white/[0.06] p-0.5 rounded-lg text-xs">
          <div
            className={clsx(
              "py-1 px-3 rounded-md transition-all",
              pin?.partMode === "VIDEO" || pin?.partMode === "ZOOM"
                ? "bg-white/10 text-white font-semibold"
                : "text-white/40",
              !(pin?.partMode === "VIDEO" || pin?.partMode === "ZOOM") &&
              canNavigate &&
              "cursor-pointer hover:text-white/60"
            )}
            onClick={() => handlePartClick(currentMl?.is_zoom ? "ZOOM" : "VIDEO")}
          >
            {partModeLabels["VIDEO"]}
          </div>
          <div
            className={clsx(
              "py-1 px-3 rounded-md transition-all",
              pin?.partMode === "PRACTICE"
                ? "bg-white/10 text-white font-semibold"
                : "text-white/40",
              pin?.partMode !== "PRACTICE" &&
              canNavigate &&
              "cursor-pointer hover:text-white/60"
            )}
            onClick={() => handlePartClick("PRACTICE")}
          >
            {partModeLabels["PRACTICE"]}
          </div>
        </div>
      )}
    </div>
  );
}
